import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';
import {
  Room,
  RoomForm,
  RoomFilters,
  GetRoomsResponse,
  RoomStatistics,
  RoomPerformance,
  RoomHistoryResponse,
  RoomStatus,
} from '../../types';

interface RoomsState {
  rooms: Room[];
  selectedRoom: Room | null;
  roomStatistics: RoomStatistics | null;
  roomPerformance: RoomPerformance | null;
  roomHistory: RoomHistoryResponse | null;
  statistics: {
    totalRooms: number;
    activeRooms: number;
    waitingRooms: number;
    playingRooms: number;
  };
  filters: RoomFilters;
  loading: boolean;
  error: string | null;
}

const initialState: RoomsState = {
  rooms: [],
  selectedRoom: null,
  roomStatistics: null,
  roomPerformance: null,
  roomHistory: null,
  statistics: {
    totalRooms: 0,
    activeRooms: 0,
    waitingRooms: 0,
    playingRooms: 0,
  },
  filters: {},
  loading: false,
  error: null,
};

// Async thunks
export const fetchRooms = createAsyncThunk(
  'rooms/fetchRooms',
  async (filters?: RoomFilters) => {
    const response = await apiService.getRooms(filters);
    return response;
  }
);

export const fetchRoom = createAsyncThunk(
  'rooms/fetchRoom',
  async (roomId: string) => {
    const response = await apiService.getRoom(roomId);
    return response;
  }
);

export const createRoom = createAsyncThunk(
  'rooms/createRoom',
  async (roomData: RoomForm) => {
    const response = await apiService.createRoom(roomData);
    return response;
  }
);

export const updateRoomStatus = createAsyncThunk(
  'rooms/updateRoomStatus',
  async ({ roomId, status, countdown }: { roomId: string; status: RoomStatus; countdown?: number }) => {
    const response = await apiService.updateRoomStatus(roomId, status, countdown);
    return response;
  }
);

export const forceStartRoom = createAsyncThunk(
  'rooms/forceStartRoom',
  async ({ roomId, reason }: { roomId: string; reason?: string }) => {
    const response = await apiService.forceStartRoom(roomId, reason);
    return response;
  }
);

export const forceEndRoom = createAsyncThunk(
  'rooms/forceEndRoom',
  async ({ roomId, reason }: { roomId: string; reason?: string }) => {
    const response = await apiService.forceEndRoom(roomId, reason);
    return response;
  }
);

export const deleteRoom = createAsyncThunk(
  'rooms/deleteRoom',
  async ({ roomId, reason }: { roomId: string; reason?: string }) => {
    const response = await apiService.deleteRoom(roomId, reason);
    return { ...response, roomId };
  }
);

export const forceDeleteRoom = createAsyncThunk(
  'rooms/forceDeleteRoom',
  async (roomId: string) => {
    const response = await apiService.forceDeleteRoom(roomId);
    return { ...response, roomId };
  }
);

export const fetchRoomStatistics = createAsyncThunk(
  'rooms/fetchRoomStatistics',
  async (roomId: string) => {
    const response = await apiService.getRoomStatistics(roomId);
    return response;
  }
);

export const fetchRoomPerformance = createAsyncThunk(
  'rooms/fetchRoomPerformance',
  async (roomId: string) => {
    const response = await apiService.getRoomPerformance(roomId);
    return response;
  }
);

export const fetchRoomHistory = createAsyncThunk(
  'rooms/fetchRoomHistory',
  async ({ roomId, page = 1, limit = 10, startDate, endDate }: { roomId: string; page?: number; limit?: number; startDate?: string; endDate?: string }) => {
    const response = await apiService.getRoomHistory(roomId, page, limit, startDate, endDate);
    return response;
  }
);

export const addPlayerToRoom = createAsyncThunk(
  'rooms/addPlayerToRoom',
  async ({ roomId, userId }: { roomId: string; userId: string }) => {
    const response = await apiService.addPlayerToRoom(roomId, userId);
    return response;
  }
);

export const removePlayerFromRoom = createAsyncThunk(
  'rooms/removePlayerFromRoom',
  async ({ roomId, userId, reason }: { roomId: string; userId: string; reason?: string }) => {
    const response = await apiService.removePlayerFromRoom(roomId, userId, reason);
    return response;
  }
);

export const generateRoomCards = createAsyncThunk(
  'rooms/generateRoomCards',
  async ({ roomId, cardCount }: { roomId: string; cardCount?: number }) => {
    const response = await apiService.generateRoomCards(roomId, cardCount);
    return { ...response, roomId };
  }
);

const roomsSlice = createSlice({
  name: 'rooms',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedRoom: (state, action: PayloadAction<Room | null>) => {
      state.selectedRoom = action.payload;
    },
    setFilters: (state, action: PayloadAction<RoomFilters>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearRoomStatistics: (state) => {
      state.roomStatistics = null;
    },
    clearRoomPerformance: (state) => {
      state.roomPerformance = null;
    },
    clearRoomHistory: (state) => {
      state.roomHistory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch rooms
      .addCase(fetchRooms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRooms.fulfilled, (state, action) => {
        state.loading = false;
        
        // Handle the response data safely
        if (action.payload) {
          state.rooms = action.payload || [];
          
          // Calculate statistics from the rooms array
          const totalRooms = action.payload.length;
          const activeRooms = action.payload.filter(room => room.status === 'playing' || room.status === 'countdown').length;
          const waitingRooms = action.payload.filter(room => room.status === 'waiting').length;
          const playingRooms = action.payload.filter(room => room.status === 'playing').length;
          
          state.statistics = {
            totalRooms,
            activeRooms,
            waitingRooms,
            playingRooms,
          };
        } else {
          // Fallback if payload is undefined
          state.rooms = [];
          state.statistics = {
            totalRooms: 0,
            activeRooms: 0,
            waitingRooms: 0,
            playingRooms: 0,
          };
        }
      })
      .addCase(fetchRooms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch rooms';
      })
      // Fetch single room
      .addCase(fetchRoom.fulfilled, (state, action) => {
        state.selectedRoom = action.payload;
      })
      // Create room
      .addCase(createRoom.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRoom.fulfilled, (state, action) => {
        state.loading = false;
        console.log('Create room fulfilled:', action.payload);
        if (action.payload) {
          state.rooms.unshift(action.payload);
          state.statistics.totalRooms += 1;
          // Update other statistics based on the new room's status
          if (action.payload.status === 'waiting') {
            state.statistics.waitingRooms += 1;
          } else if (action.payload.status === 'playing' || action.payload.status === 'countdown') {
            state.statistics.activeRooms += 1;
          }
          if (action.payload.status === 'playing') {
            state.statistics.playingRooms += 1;
          }
        } else {
          console.error('Create room failed in reducer:', action.payload);
        }
      })
      .addCase(createRoom.rejected, (state, action) => {
        state.loading = false;
        console.error('Create room rejected:', action.error);
        state.error = action.error.message || 'Failed to create room';
      })
      // Update room status
      .addCase(updateRoomStatus.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.rooms.findIndex(r => r._id === action.payload.room._id);
          if (index !== -1) {
            state.rooms[index] = action.payload.room;
          }
          if (state.selectedRoom?._id === action.payload.room._id) {
            state.selectedRoom = action.payload.room;
          }
        }
      })
      // Force start room
      .addCase(forceStartRoom.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.rooms.findIndex(r => r._id === action.payload.room._id);
          if (index !== -1) {
            state.rooms[index] = action.payload.room;
          }
          if (state.selectedRoom?._id === action.payload.room._id) {
            state.selectedRoom = action.payload.room;
          }
        }
      })
      // Force end room
      .addCase(forceEndRoom.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.rooms.findIndex(r => r._id === action.payload.room._id);
          if (index !== -1) {
            state.rooms[index] = action.payload.room;
          }
          if (state.selectedRoom?._id === action.payload.room._id) {
            state.selectedRoom = action.payload.room;
          }
        }
      })
      // Delete room
      .addCase(deleteRoom.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.rooms = state.rooms.filter(r => r._id !== action.payload.roomId);
          if (state.selectedRoom?._id === action.payload.roomId) {
            state.selectedRoom = null;
          }
          state.statistics.totalRooms -= 1;
        }
      })
      // Force delete room
      .addCase(forceDeleteRoom.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.rooms = state.rooms.filter(r => r._id !== action.payload.roomId);
          if (state.selectedRoom?._id === action.payload.roomId) {
            state.selectedRoom = null;
          }
          state.statistics.totalRooms -= 1;
        }
      })
      // Fetch room statistics
      .addCase(fetchRoomStatistics.fulfilled, (state, action) => {
        state.roomStatistics = action.payload;
      })
      // Fetch room performance
      .addCase(fetchRoomPerformance.fulfilled, (state, action) => {
        state.roomPerformance = action.payload;
      })
      // Fetch room history
      .addCase(fetchRoomHistory.fulfilled, (state, action) => {
        state.roomHistory = action.payload;
      })
      // Add player to room
      .addCase(addPlayerToRoom.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.rooms.findIndex(r => r._id === action.payload.room._id);
          if (index !== -1) {
            state.rooms[index] = action.payload.room;
          }
          if (state.selectedRoom?._id === action.payload.room._id) {
            state.selectedRoom = action.payload.room;
          }
        }
      })
      // Remove player from room
      .addCase(removePlayerFromRoom.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.rooms.findIndex(r => r._id === action.payload.room._id);
          if (index !== -1) {
            state.rooms[index] = action.payload.room;
          }
          if (state.selectedRoom?._id === action.payload.room._id) {
            state.selectedRoom = action.payload.room;
          }
        }
      });
  },
});

export const {
  clearError,
  setSelectedRoom,
  setFilters,
  clearFilters,
  clearRoomStatistics,
  clearRoomPerformance,
  clearRoomHistory,
} = roomsSlice.actions;

export default roomsSlice.reducer; 